import React, { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import {
  getVisitByID,
  getWeeklyAssignmentByVisit,
  updateVisit,
} from "../../../api/services/scheduling";
import { getCheckList, getClientPaymentServices } from "../../../api/services/core";
import { getActiveCarePlans } from "../../../api/services/carePathWay";
import FundingModal from "../../../components/Clients/Funding/FundingModal";
import Loader from "../../../components/Loader/Loader";
import VisitDetailsForm from "./EditSchedule/VisitDetailsForm";
import SchedulingForm from "./EditSchedule/SchedulingForm";
import SafetyCompliance from "./EditSchedule/SafetyCompliance";
import FormActions from "./EditSchedule/FormActions";

const EditSchedule = ({ visitId, clientId, onHide, onSuccess }) => {
  const [formData, setFormData] = useState({
    visit_name: "",
    care_plan: "",
    payment_service: "",
    carers_required: 1,
    personalized_checks: [],
    checklist: [],
    support_instructions: "",
    start_time: "",
    end_time: null,
    repeat_every: 0,
    time_critical: false,
    time_tolerance: 60,
    mandatory_notes: false,
    allow_photos: false,
    share_with_family: false,
    continue_indefinitely: false,
  });
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [customTimeData, setCustomTimeData] = useState({});
  const [totalWeeklyHours, setTotalWeeklyHours] = useState("0h 0m");
  const [isTimeCritical, setIsTimeCritical] = useState(false);
  const [isContinueIndifinite, setIsContinueIndifinite] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedCheckList, setSelectedCheckList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removedDays, setRemovedDays] = useState([]);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const { data: weeklyAssignment, isLoading: assignmentLoading } = useQuery(
    [QK.weeklyAssignment, visitId],
    () => getWeeklyAssignmentByVisit(visitId),
    { enabled: !!visitId }
  );

  const { data: visitDetails, isLoading: visitLoading } = useQuery(
    [QK.visitDetails, visitId],
    () => getVisitByID(visitId),
    { enabled: !!visitId }
  );

  const { data: checkList = [] } = useQuery(
    [QK.checklist],
    () => getCheckList()
  );

  const { data: paymentServices } = useQuery(
    [QK.clientPaymentServices, clientId],
    () => getClientPaymentServices(clientId),
    { enabled: !!clientId }
  );

  const { data: activeCarePlans = [], isLoading: carePlanLoading } = useQuery(
    [QK.activeCarePlans, { client: clientId }],
    () => getActiveCarePlans({ client: clientId }),
    { enabled: !!clientId }
  );

  const carePlanOptions =
    activeCarePlans?.map((plan) => ({
      value: plan.id,
      label: plan.form_title || plan.title || `Care Plan ${plan.id}`,
    })) || [];

  const paymentServiceOptions =
    paymentServices?.map((p) => ({
      value: p.id || p.value,
      label: p.service || p.name || `Service ${p.id}`,
    })) || [];

  const formattedCheckList = Array.isArray(checkList)
    ? checkList.map((option) => ({
        value: option.id,
        label: option.name,
      }))
    : [];

  const formatTotalMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const calculateTotalWeeklyHours = (timeSource) => {
    let totalMinutes = 0;

    Object.values(timeSource || {}).forEach((slots) => {
      if (Array.isArray(slots)) {
        slots.forEach((slot) => {
          if (slot.time_required_from && slot.time_required_to) {
            const [fH, fM] = slot.time_required_from.split(":").map(Number);
            const [tH, tM] = slot.time_required_to.split(":").map(Number);
            let fromMin = fH * 60 + fM;
            let toMin = tH * 60 + tM;
            if (toMin < fromMin) toMin += 1440;
            totalMinutes += toMin - fromMin;
          }
        });
      }
    });

    setTotalWeeklyHours(formatTotalMinutes(totalMinutes));
    setFormData((prev) => ({ ...prev, total_duration: totalMinutes }));
  };

  useEffect(() => {
    if (visitDetails) {
      setFormData({
        visit_name: visitDetails.visit_name || "",
        care_plan: visitDetails.care_plan || "",
        payment_service: visitDetails.payment_service || "",
        carers_required: visitDetails.carers_required || 1,
        personalized_checks: Array.isArray(visitDetails.personalized_checks)
          ? visitDetails.personalized_checks.map((c) => ({
              id: c.id ?? null,
              check_name: c.check_name || "",
            }))
          : [],
        checklist: visitDetails.checklist || [],
        support_instructions: visitDetails.support_instructions || "",
        start_time: visitDetails.start_time || "",
        end_time: visitDetails.end_time || null,
        repeat_every: visitDetails.repeat_every || 0,
        time_critical: visitDetails.time_critical || false,
        time_tolerance: visitDetails.time_tolerance || 60,
        mandatory_notes: visitDetails.mandatory_notes || false,
        allow_photos: visitDetails.allow_photos || false,
        share_with_family: visitDetails.share_with_family || false,
        continue_indefinitely: visitDetails.continue_indefinitely || false,
      });

      setIsTimeCritical(visitDetails.time_critical || false);
      setIsContinueIndifinite(visitDetails.continue_indefinitely || false);

      if (Array.isArray(visitDetails?.checklist_details)) {
        const formatted = visitDetails.checklist_details.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setSelectedCheckList(formatted);
      }
    }
  }, [visitDetails]);

  useEffect(() => {
    if (Array.isArray(weeklyAssignment) && weeklyAssignment.length > 0) {
      const updatedCustomTimeData = {};
      const weekdays = [];

      weeklyAssignment.forEach((job) => {
        const day = job.weekly_day;
        if (!day) return;
        weekdays.push(day);

        if (!updatedCustomTimeData[day]) {
          updatedCustomTimeData[day] = [];
        }

        updatedCustomTimeData[day].push({
          id: job.id,
          time_required_from: job.time_required_from,
          time_required_to: job.time_required_to,
        });
      });

      setCustomTimeData(updatedCustomTimeData);
      setSelectedDays([...new Set(weekdays)]);
      setFormData((prev) => ({
        ...prev,
        weekly_schedule_data: updatedCustomTimeData,
      }));
      calculateTotalWeeklyHours(updatedCustomTimeData);
    }
  }, [weeklyAssignment]);

  const handleChange = (e) => {
    const { name, checked, type, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    const finalValue = type === "number" ? Number(value) : newValue;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleCheckBox = (name, checked) => {
    if (name === "continue_indefinitely") {
      setIsContinueIndifinite(checked);
      setFormData((prev) => ({ ...prev, end_time: null }));
    }
    if (name === "time_critical") {
      setIsTimeCritical(checked);
    }
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleMultiSelectChange = (options) => {
    setSelectedCheckList(options);
  };

  const handleAddInput = () => {
    setFormData((prev) => ({
      ...prev,
      personalized_checks: [
        ...(prev.personalized_checks || []),
        { id: null, check_name: "" },
      ],
    }));
  };

  const handleRemoveInput = (index) => {
    setFormData((prev) => ({
      ...prev,
      personalized_checks: (prev.personalized_checks || []).filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handlePersonalizedCheck = (index, e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const newChecks = [...(prev.personalized_checks || [])];
      newChecks[index] = { ...newChecks[index], check_name: value };
      return { ...prev, personalized_checks: newChecks };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const cleanedPersonalizedChecks = (formData.personalized_checks || [])
      .filter((c) => (c.check_name || "").trim())
      .map((c) => ({
        id: c.id ?? null,
        check_name: c.check_name.trim(),
      }));

    const cleanedChecklist = (selectedCheckList || [])
      .map((item) => item?.value)
      .filter(Boolean);

    const requestData = {
      ...formData,
      personalized_checks: cleanedPersonalizedChecks,
      checklist: cleanedChecklist,
      weekly_schedule_data: customTimeData || {},
      removed_days: removedDays || [],
    };

    try {
      const response = await updateVisit(visitId, requestData);
      if (response.status === 200) {
        enqueueSnackbar("Visit updated successfully.", { variant: "success" });
        queryClient.invalidateQueries([QK.visits]);
        queryClient.invalidateQueries([QK.visitPlan]);
        queryClient.invalidateQueries([QK.weeklyAssignment]);
        queryClient.invalidateQueries([QK.visitDetails]);

        if (typeof onSuccess === "function") {
          setTimeout(() => onSuccess(), 600);
        } else {
          setTimeout(() => onHide(), 800);
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to update visit.",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (assignmentLoading || visitLoading || carePlanLoading) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: { xs: 1, sm: 2 },
          pb: 10,
        }}
      >
        <form onSubmit={handleSubmit} id="edit-schedule-form">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ flex: "1 1 48%", minWidth: "300px" }}>
              <VisitDetailsForm
                formData={formData}
                handleChange={handleChange}
                handleCheckBox={handleCheckBox}
                carePlanOptions={carePlanOptions}
                paymentServiceOptions={paymentServiceOptions}
                selectedCheckList={selectedCheckList}
                handleMultiSelectChange={handleMultiSelectChange}
                formattedCheckList={formattedCheckList}
                handleAddInput={handleAddInput}
                handleRemoveInput={handleRemoveInput}
                handlePersonalizedCheck={handlePersonalizedCheck}
                isContinueIndifinite={isContinueIndifinite}
                setShowFundingModal={setShowFundingModal}
              />
            </Box>

            <Box sx={{ flex: "1 1 48%", minWidth: "300px" }}>
              <Stack spacing={2}>
                <SchedulingForm
                  formData={formData}
                  handleChange={handleChange}
                  handleCheckBox={handleCheckBox}
                  selectedDays={selectedDays}
                  setSelectedDays={setSelectedDays}
                  customTimeData={customTimeData}
                  setCustomTimeData={setCustomTimeData}
                  setFormData={setFormData}
                  calculateTotalWeeklyHours={calculateTotalWeeklyHours}
                  setRemovedDays={setRemovedDays}
                  weeklyAssignment={weeklyAssignment}
                  totalWeeklyHours={totalWeeklyHours}
                  isTimeCritical={isTimeCritical}
                  daysOfWeek={daysOfWeek}
                />

                <SafetyCompliance
                  formData={formData}
                  handleCheckBox={handleCheckBox}
                />
              </Stack>
            </Box>
          </Box>
        </form>
      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #F2F4F7",
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          zIndex: 10,
          boxShadow: "0 -4px 12px rgba(16, 24, 40, 0.04)",
          flexShrink: 0,
        }}
      >
        <FormActions
          isSubmitting={isSubmitting}
          onCancel={onHide}
          onSubmit={handleSubmit}
        />
      </Box>

      <FundingModal
        show={showFundingModal}
        onHide={() => setShowFundingModal(false)}
        clientID={clientId}
      />
    </Box>
  );
};

export default EditSchedule;